import ListItem from "@/components/ListItem/ListItem";
import { Path } from "@/router";
import {
  getFacilityMatchHeadline,
  getFacilityMatchSupportingContent,
  getPersonMatchHeadline,
  getPersonMatchSupportingContent,
  getSectionMatchHeadline,
  isPersonCrmIdOnlyMatch,
  type ListItemContent,
  type VisiblePersonAttribute
} from "./matchContext";
import { getPersonDisplayName } from "./personSearchConfig";
import {
  getActivityDateRange,
  getActivityDisplayName,
  getActivityHitPath,
  getActivityListIcon,
  getActivityListOverline
} from "./activitySearchConfig";
import { PersonHit, FacilityHit, SectionHit, ActivityHit, GlobalHit } from "./types";
import { formatLocationText } from "./utils";

/**
 * Helper function to get the display name for any global search object type
 */
export const getGlobalDisplayName = (hit: GlobalHit): string => {
  switch (hit.object_type) {
    case "Person":
      return getPersonDisplayName(hit as PersonHit);
    case "Facility":
      const facilityHit = hit as FacilityHit;
      return facilityHit.facility_name || facilityHit.name || "Unknown Facility";
    case "Section":
      const sectionHit = hit as SectionHit;
      return sectionHit.section_name || sectionHit.name || "Unknown Section";
    case "Activity":
      return getActivityDisplayName(hit as ActivityHit);
    default:
      return (
        (hit as any).name ||
        (hit as any).preferred_name ||
        (hit as any).facility_name ||
        (hit as any).section_name ||
        "Unknown"
      );
  }
};

export type GlobalHitListItemContent = {
  headline: ListItemContent;
  supportingText: ListItemContent;
  longContent: boolean;
  picture?: string;
  iconName?: "store" | "domain" | "today" | "assignment";
  overline?: string;
};

export type GlobalHitPresentationOptions = {
  personVisibleAttributes?: readonly VisiblePersonAttribute[];
};

export function getGlobalHitListItemContent(
  hit: GlobalHit,
  query?: string,
  options: GlobalHitPresentationOptions = {}
): GlobalHitListItemContent | null {
  switch (hit.object_type) {
    case "Person": {
      const personHit = hit as PersonHit;
      const supportingText = getPersonMatchSupportingContent(personHit, {
        visibleAttributes: options.personVisibleAttributes,
        query
      });
      return {
        headline: getPersonMatchHeadline(personHit, getPersonDisplayName(personHit)),
        supportingText,
        longContent: !!supportingText,
        picture: personHit.profile_photo
      };
    }

    case "Facility": {
      const facilityHit = hit as FacilityHit;
      const supportingText = getFacilityMatchSupportingContent(facilityHit, query);
      return {
        headline: getFacilityMatchHeadline(facilityHit),
        supportingText,
        longContent: !!supportingText,
        iconName: "store"
      };
    }

    case "Section": {
      const sectionHit = hit as SectionHit;
      return {
        headline: getSectionMatchHeadline(sectionHit),
        supportingText: formatLocationText(sectionHit.city, sectionHit.state) || undefined,
        longContent: false,
        iconName: "domain"
      };
    }

    case "Activity": {
      const activityHit = hit as ActivityHit;
      const supportingText = getActivityDateRange(activityHit);
      return {
        headline: getActivityDisplayName(activityHit),
        supportingText,
        longContent: !!supportingText,
        iconName: getActivityListIcon(activityHit),
        overline: getActivityListOverline(activityHit)
      };
    }

    default:
      return null;
  }
}

/**
 * Helper function to render global search hits with appropriate styling
 */
export const renderGlobalHit = (hit: GlobalHit, select: (hit: GlobalHit) => void, query?: string) => {
  const content = getGlobalHitListItemContent(hit, query);
  if (!content) {
    return (
      <div key={hit.objectID} className="cursor-pointer" onClick={() => select(hit)}>
        <ListItem compact useAvatar headline={(hit as any).name || "Unknown"} />
      </div>
    );
  }

  return (
    <div key={hit.objectID} className="cursor-pointer" onClick={() => select(hit)}>
      <ListItem
        compact
        useAvatar
        picture={content.picture}
        iconName={content.iconName}
        overline={content.overline}
        headline={content.headline}
        supportingText={content.supportingText}
        longContent={content.longContent}
      />
    </div>
  );
};

export function filterGlobalSearchHits(hits: GlobalHit[], query: string): GlobalHit[] {
  return hits.filter((hit) => hit.object_type !== "Person" || !isPersonCrmIdOnlyMatch(hit as PersonHit, query));
}

/**
 * Checks whether a search hit is an exact match for the given query.
 * Compares against PGA ID, legacy ID, and section ID
 * using a case-insensitive, trimmed comparison.
 */
export const isGlobalExactMatch = (hit: GlobalHit, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return false;

  switch (hit.object_type) {
    case "Person": {
      const person = hit as PersonHit;
      return person.pga_id?.toLowerCase() === q;
    }
    case "Facility": {
      const facility = hit as FacilityHit;
      return facility.legacy_id?.toLowerCase() === q;
    }
    case "Section": {
      const section = hit as SectionHit;
      return section.section_id?.toLowerCase() === q;
    }
    default:
      return false;
  }
};

export function navigateGlobalHit(hit: GlobalHit, navigate: (path: Path) => void) {
  switch (hit.object_type) {
    case "Person": {
      const personId = (hit as PersonHit).pga_id || (hit as PersonHit).legacy_id;
      if (personId) navigate(`/people/${personId}` as Path);
      break;
    }
    case "Facility":
      navigate(`/facilities/${(hit as FacilityHit).facility_id}` as Path);
      break;
    case "Section":
      navigate(`/sections/${(hit as SectionHit).section_id}` as Path);
      break;
    case "Activity": {
      const path = getActivityHitPath(hit as ActivityHit);
      if (path) navigate(path);
      break;
    }
  }
}

/**
 * Helper function to create initial UI state for global search
 */
export const createGlobalSearchUiState = (indexName: string) => {
  return {
    [indexName]: {
      configure: {
        filters: "object_type:Person OR object_type:Facility OR object_type:Section",
        restrictSearchableAttributes: [
          "name",
          "preferred_name",
          "facility_name",
          "section_name",
          "pga_id",
          "phone",
          "phones",
          "legacy_id",
          "section_id",
          "email"
        ],
        attributesToHighlight: [
          "name",
          "preferred_name",
          "facility_name",
          "section_name",
          "pga_id",
          "phone",
          "phones",
          "legacy_id",
          "email",
          "section_id"
        ]
      }
    }
  };
};
